import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import Drawer from '../Common/Drawer/Drawer';
import SalesforceTaskCreateForm from './SalesforceTaskCreateForm';
import salesforceTasksService from 'services/salesforceTasksService';
import { useNotificationContext } from 'contexts/NotificationContext';

// Standalone "Create Task" drawer for creating a CRM task related directly to
// a Salesforce record (e.g. a Fundraising), outside of the alerts flow.
const SalesforceTaskCreateDrawer = ({
	open,
	relatedRecord,
	onClose,
	onCreated,
	onCreateTaskClick,
}) => {
	const { showNotification } = useNotificationContext();

	const [formOptions, setFormOptions] = useState(null);
	const [formOptionsLoading, setFormOptionsLoading] = useState(true);

	// The form publishes its Cancel / Create Task buttons for the drawer footer
	const [footerActions, setFooterActions] = useState(null);

	// The form re-publishes its footer buttons whenever its callbacks change, so
	// keep them stable (reading the latest values through a ref) to avoid a render
	// loop when the parent passes inline handlers. This also keeps the options
	// request from re-running if showNotification is not referentially stable.
	const callbacksRef = useRef({ onClose, onCreated, showNotification });
	callbacksRef.current = { onClose, onCreated, showNotification };

	const handleClose = useCallback(() => callbacksRef.current.onClose(), []);

	const handleCreated = useCallback(async (response) => {
		await callbacksRef.current.onCreated?.(response);
		callbacksRef.current.onClose();
	}, []);

	useEffect(() => {
		if (!open || formOptions) {
			return;
		}

		// Prevent stale async results from updating the form after this effect is cleaned up
		let isCurrent = true;

		const loadFormOptions = async () => {
			try {
				setFormOptionsLoading(true);

				const options = await salesforceTasksService.getFormOptions();

				if (isCurrent) {
					setFormOptions(options);
				}
			} catch (error) {
				console.error('Failed to load Salesforce task form options:', error);

				if (isCurrent) {
					callbacksRef.current.showNotification(
						'Unable to load task options',
						'error'
					);
					handleClose();
				}
			} finally {
				if (isCurrent) {
					setFormOptionsLoading(false);
				}
			}
		};

		loadFormOptions();

		return () => {
			isCurrent = false;
		};
	}, [open, formOptions, handleClose]);

	return (
		<Drawer
			open={open}
			onClose={handleClose}
			title='Create Task'
			actionButtons={footerActions ?? []}
		>
			<SalesforceTaskCreateForm
				relatedRecord={relatedRecord}
				formOptions={formOptions}
				formOptionsLoading={formOptionsLoading}
				onCancel={handleClose}
				onCreateTaskClick={onCreateTaskClick}
				onCreated={handleCreated}
				onFooterActionsChange={setFooterActions}
			/>
		</Drawer>
	);
};

SalesforceTaskCreateDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	relatedRecord: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string,
	}).isRequired,
	onClose: PropTypes.func.isRequired,
	onCreated: PropTypes.func,
	onCreateTaskClick: PropTypes.func,
};

export default SalesforceTaskCreateDrawer;
